'use client'

import React, { useState, useCallback, useEffect } from 'react'
import Link from 'next/link'
import { Button } from './ui/button'
import { Bell, X } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { useSession } from '@/features/auth/context/session.context'
import { useNotificationsSocket } from '@/shared/hooks/use-notifications-socket'
import { authFetch, authFetchJson } from '@/shared/lib/auth-fetch'

export type NotificationItem = {
  id: number
  message: string
  title?: string | null
  type: string
  isRead: boolean
  createdAt: string | number
  /** Optional path to the page where the action occurred (e.g. group dashboard). When set, show a link. */
  actionPath?: string | null
  /** Optional link text (e.g. homeowner name for project notes). When set, used as the link label. */
  actionLabel?: string | null
}

type NotificationsResponse = {
  success?: boolean
  data?: NotificationItem[]
}

function normalizePayload(payload: unknown): NotificationItem | null {
  if (payload == null || typeof payload !== 'object') return null
  const p = payload as Record<string, unknown>
  const id = typeof p.id === 'number' ? p.id : null
  const message = typeof p.message === 'string' ? p.message : ''
  if (id == null) return null
  const title = p.title != null && typeof p.title === 'string' ? p.title : null
  const type = typeof p.type === 'string' ? p.type : 'general'
  const isRead = p.isRead === true
  const actionPath =
    p.actionPath != null && typeof p.actionPath === 'string' && p.actionPath.trim() !== ''
      ? p.actionPath.trim()
      : null
  const actionLabel =
    p.actionLabel != null && typeof p.actionLabel === 'string' && p.actionLabel.trim() !== ''
      ? p.actionLabel.trim()
      : null
  const createdAt =
    p.createdAt instanceof Date
      ? p.createdAt.toISOString()
      : typeof p.createdAt === 'number'
        ? p.createdAt
        : typeof p.createdAt === 'string'
          ? p.createdAt
          : new Date().toISOString()
  return { id, message, title, type, isRead, createdAt, actionPath, actionLabel }
}

/**
 * Parse notification date so it displays in the user's local timezone (e.g. CST).
 * Backend sends UTC (ISO with Z). If we ever get "2026-02-21T22:15:00" without "Z",
 * JS would parse as local and show wrong time; we force UTC by appending Z.
 */
function parseNotificationDate(createdAt: string | number): Date {
  if (createdAt == null) return new Date()
  if (typeof createdAt === 'number') return new Date(createdAt)
  const s = String(createdAt).trim()
  if (!s) return new Date()
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s)
  const normalized = s.replace(/\s+/, 'T').replace(/\.(\d{3})\d*$/, '.$1')
  const withZ = normalized.includes('.') ? normalized + 'Z' : normalized + '.000Z'
  return new Date(withZ)
}

function formatNotificationDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    dateStyle: 'short',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })
}

/** Extract group name from "tagged you in a note for group \"X\"." for use as link text. */
function getGroupNameFromMessage(message: string): string | null {
  const match = message.match(/for group\s+"([^"]+)"/i)
  return match?.[1]?.trim() ?? null
}

/** Link text for notification action: homeowner name for project notes when actionLabel set, else "View Homeowner"; group name or "View group" for group notes. */
function getNotificationLinkText(notification: NotificationItem): string {
  // If we have an explicit actionLabel (e.g. homeowner name), use it
  if (notification.actionLabel != null && String(notification.actionLabel).trim() !== '') {
    return String(notification.actionLabel).trim()
  }
  // Project note: title, message, or link to homeowner page
  const isProjectNote =
    (notification.title != null && String(notification.title).includes('Project note')) ||
    (notification.message != null && String(notification.message).includes('Project General Notes')) ||
    (notification.actionPath != null && String(notification.actionPath).includes('/homeowners/'))
  if (isProjectNote) {
    return 'View Homeowner'
  }
  return getGroupNameFromMessage(notification.message ?? '') ?? 'View group'
}

function NotificationButton() {
  const { session, profile } = useSession()
  const userId = session?.sub ?? profile?.id
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const handleLiveNotification = useCallback((payload: unknown) => {
    const item = normalizePayload(payload)
    if (!item) return
    setNotifications((prev) => {
      const seen = new Set(prev.map((n) => n.id))
      if (seen.has(item.id)) return prev
      return [item, ...prev]
    })
  }, [])

  useNotificationsSocket(userId ?? null, handleLiveNotification)

  const fetchNotifications = useCallback(async () => {
    if (userId == null) return
    setLoading(true)
    try {
      const res = await authFetchJson<NotificationItem[]>(`/notifications/user/${userId}`)
      const list = res?.success && Array.isArray(res.data) ? res.data : []
      setNotifications(list)
    } finally {
      setLoading(false)
    }
  }, [userId])

  // Fetch on mount so unread count badge shows without opening the dropdown
  useEffect(() => {
    if (userId != null) fetchNotifications()
  }, [userId, fetchNotifications])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next)
      if (next && userId != null) fetchNotifications()
    },
    [userId, fetchNotifications]
  )

  const handleMarkAllRead = useCallback(async () => {
    if (userId == null) return
    try {
      await authFetch(`/notifications/mark-as-read/${userId}`, {
        method: 'POST',
      })
      await fetchNotifications()
    } catch {
      // ignore
    }
  }, [userId, fetchNotifications])

  const handleRemove = useCallback(async (notificationId: number) => {
    try {
      const res = await authFetch(`/notifications/${notificationId}`, {
        method: 'DELETE',
      })
      if (res.ok) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId))
      }
    } catch {
      // ignore
    }
  }, [])

  const handleMarkOneAsRead = useCallback(async (notificationId: number) => {
    try {
      const res = await authFetch(`/notifications/${notificationId}/mark-as-read`, {
        method: 'POST',
      })
      if (res.ok) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
        )
      }
    } catch {
      // ignore
    }
  }, [])

  const unreadCount = notifications.filter((n) => !n.isRead).length

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="flex relative">
          <Bell className="w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="p-5 min-w-[400px] max-w-[90vw]"
        side="bottom"
        align="end"
      >
        <div className="space-y-3">
          <div className="flex justify-between items-center gap-4">
            <h3 className="text-xl font-semibold">Notifications</h3>
            {notifications.length > 0 && (
              <button
                type="button"
                className="text-primary underline text-sm shrink-0"
                onClick={handleMarkAllRead}
              >
                Mark all as read
              </button>
            )}
          </div>
          {loading ? (
            <p className="text-sm text-muted-foreground py-4">Loading…</p>
          ) : notifications.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No notifications yet.</p>
          ) : (
            <ul className="max-h-[320px] overflow-y-auto space-y-2">
              {notifications.map((n) => (
                <li
                  key={n.id}
                  className={`rounded-md border p-3 text-sm pr-9 relative ${n.isRead ? 'bg-muted/50 border-transparent' : 'border-input'}`}
                >
                  <button
                    type="button"
                    aria-label="Remove notification"
                    className="absolute top-2 right-2 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                    onClick={() => handleRemove(n.id)}
                  >
                    <X className="h-4 w-4" />
                  </button>
                  {n.title && (
                    <p className="font-medium text-foreground mb-0.5">{n.title}</p>
                  )}
                  <p className="text-muted-foreground">{n.message}</p>
                  {n.actionPath && (
                    <p className="mt-1.5">
                      <Link
                        href={n.actionPath}
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline font-medium"
                        onClick={() => {
                          if (!n.isRead) handleMarkOneAsRead(n.id)
                          setOpen(false)
                        }}
                      >
                        {getNotificationLinkText(n)} →
                      </Link>
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {n.createdAt ? formatNotificationDate(parseNotificationDate(n.createdAt)) : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default NotificationButton
